import { supabase } from '../lib/supabase';
import type { User, Session } from '@supabase/supabase-js';
import type { Organization, OrganizationMember, Database } from '../types/database.types';

export interface AuthUser extends User {
  organizations?: OrganizationMember[];
  current_organization?: Organization;
  role?: string;
}

export interface SignUpData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  organizationName?: string;
  phone?: string;
}

export interface SignInData {
  email: string;
  password: string;
}

class AuthService {
  // Get current session
  async getCurrentSession(): Promise<{ session: Session | null; user: AuthUser | null }> {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error('Error getting session:', error);
      return { session: null, user: null };
    }

    if (!session?.user) {
      return { session, user: null };
    }

    // Get user's organizations
    const user = await this.enrichUserWithOrganizations(session.user);
    return { session, user };
  }

  // Sign up new user with organization
  async signUp(userData: SignUpData) {
    const { email, password, firstName, lastName, organizationName, phone } = userData;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: `${firstName} ${lastName}`,
          first_name: firstName,
          last_name: lastName,
          phone,
          organization_name: organizationName || `${firstName}'s Dealership`,
        }
      }
    });

    if (error) {
      console.error('Sign up error:', error);
      throw error;
    }

    return data;
  }

  // Sign in existing user
  async signIn(credentials: SignInData) {
    const { email, password } = credentials;
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      console.error('Sign in error:', error);
      throw error;
    }

    if (data.user) {
      const user = await this.enrichUserWithOrganizations(data.user);
      return { ...data, user };
    }

    return data;
  }

  // Sign out
  async signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  }

  // Get user's organizations and set current organization
  private async enrichUserWithOrganizations(user: User): Promise<AuthUser> {
    try {
      // Get user's organization from users table
      // First try with the join, if that fails, do separate queries
      let userData: any = null;
      let currentOrganization: any = null;
      
      // Try the join query first
      const { data: joinData, error: joinError } = await supabase
        .from('users')
        .select(`
          *,
          organization:organizations(*)
        `)
        .eq('id', user.id)
        .single();

      if (!joinError && joinData) {
        userData = joinData;
        currentOrganization = joinData.organization;
      } else {
        // Fall back to separate queries
        const { data: userOnly, error: userOnlyError } = await supabase
          .from('users')
          .select('*')
          .eq('id', user.id)
          .single();
          
        if (userOnlyError) {
          console.error('Error fetching user data:', userOnlyError);
          return user as AuthUser;
        }
        
        userData = userOnly;
        
        // Get organization separately if we have an org ID
        if (userData?.organization_id) {
          const { data: orgData } = await supabase
            .from('organizations')
            .select('*')
            .eq('id', userData.organization_id)
            .single();
            
          currentOrganization = orgData;
        }
      }

      
      // If user has no organization, create one automatically
      if (!currentOrganization && userData) {
        console.log('User has no organization, creating one automatically...');
        
        // Extract user info for organization name
        const firstName = user.user_metadata?.first_name || user.user_metadata?.firstName || 'User';
        const lastName = user.user_metadata?.last_name || user.user_metadata?.lastName || '';
        const organizationName = firstName !== 'User' && lastName 
          ? `${firstName} ${lastName}'s Dealership`
          : `${firstName}'s Dealership`;

        // Create organization
        const { data: newOrg, error: orgError } = await supabase
          .from('organizations')
          .insert([
            {
              name: organizationName,
              slug: 'dealership-' + Date.now() + '-' + Math.random().toString(36).substring(2, 7),
              email: user.email || 'info@dealership.com',
              subscription_tier: 'trial'
            }
          ])
          .select()
          .single();

        if (orgError) {
          console.error('Failed to create organization:', orgError);
        } else {
          // Update user with organization
          const { error: updateError } = await supabase
            .from('users')
            .update({
              organization_id: newOrg.id,
              role: 'owner'
            })
            .eq('id', user.id);

          if (updateError) {
            console.error('Failed to update user with organization:', updateError);
          } else {
            // Update our local data
            currentOrganization = newOrg;
            console.log('✅ Successfully created organization for user:', organizationName);
          }
        }
      }
      
      // Return enriched user
      return {
        ...user,
        organizations: currentOrganization ? [currentOrganization] : [],
        current_organization: currentOrganization,
        organization_id: userData?.organization_id,
        role: userData?.role || 'owner'
      } as AuthUser;
    } catch (error) {
      console.error('Error enriching user:', error);
      return user as AuthUser;
    }
  }

  // Switch current organization
  async switchOrganization(organizationId: string): Promise<Organization | null> {
    try {
      // Check if user has access to this organization
      const { data: userData, error } = await supabase
        .from('users')
        .select(`
          *,
          organization:organizations(*)
        `)
        .eq('id', (await this.getCurrentSession()).user?.id)
        .eq('organization_id', organizationId)
        .single();

      if (error || !userData) {
        throw new Error('You do not have access to this organization');
      }

      return userData.organization as Organization;
    } catch (error) {
      console.error('Error switching organization:', error);
      throw error;
    }
  }

  // Invite user to organization
  async inviteUser(email: string, role: string, organizationId: string) {
    try {
      // Check if user already exists
      const { data: existingUser } = await supabase
        .from('users')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('id', (await supabase.auth.getUser()).data.user?.id);

      if (existingUser && existingUser.length > 0) {
        throw new Error('User is already a member of this organization');
      }

      // For now, just create a placeholder - in production you'd send an email
      // and handle the invitation flow properly
      console.log(`Invitation would be sent to ${email} for role ${role}`);
      
      return { success: true, message: 'Invitation sent successfully' };
    } catch (error) {
      console.error('Error inviting user:', error);
      throw error;
    }
  }

  // Listen to auth state changes
  onAuthStateChange(callback: (session: Session | null) => void) {
    return supabase.auth.onAuthStateChange(async (event, session) => {
      callback(session);
    });
  }

  // Reset password
  async resetPassword(email: string) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  }

  // Update password
  async updatePassword(newPassword: string) {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) {
      console.error('Update password error:', error);
      throw error;
    }
  }

  // Update user profile
  async updateProfile(updates: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    avatar_url?: string;
  }) {
    const { error } = await supabase.auth.updateUser({
      data: {
        first_name: updates.firstName,
        last_name: updates.lastName,
        full_name: updates.firstName && updates.lastName ? 
          `${updates.firstName} ${updates.lastName}` : undefined,
        phone: updates.phone,
        avatar_url: updates.avatar_url,
      }
    });

    if (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  }

  // Get organization details
  async getOrganization(organizationId: string): Promise<Organization | null> {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', organizationId)
        .single();

      if (error) {
        console.error('Error fetching organization:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error getting organization:', error);
      return null;
    }
  }

  // Update organization
  async updateOrganization(organizationId: string, updates: Partial<Organization>) {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', organizationId)
        .select()
        .single();

      if (error) {
        console.error('Error updating organization:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error updating organization:', error);
      throw error;
    }
  }

  // Get organization members (users in the same organization)
  async getOrganizationMembers(organizationId: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('organization_id', organizationId);

      if (error) {
        console.error('Error fetching organization members:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error getting organization members:', error);
      throw error;
    }
  }

  // Test database connection
  async testConnection(): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('id')
        .limit(1);

      if (error) {
        console.error('Database connection test failed:', error);
        return false;
      }

      console.log('✅ Database connection successful!');
      return true;
    } catch (error) {
      console.error('Database connection test error:', error);
      return false;
    }
  }
}

export const authService = new AuthService();